import { motion, AnimatePresence } from 'motion/react'
import { useState, useEffect, useCallback } from 'react'
import { getAllTiles, saveTile, deleteTile, saveSession, generateTileId, createEmptyTile, Tile } from '@/lib/storage'

const PRIORITY_COLORS = {
  high: 'bg-red-400',
  medium: 'bg-amber-400',
  low: 'bg-green-400',
} as const

export function InboxView() {
  const [tiles, setTiles] = useState<Tile[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showAddModal, setShowAddModal] = useState(false)
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskProject, setNewTaskProject] = useState('')
  const [newTaskPriority, setNewTaskPriority] = useState<'high' | 'medium' | 'low'>('medium')

  const loadTiles = useCallback(async () => {
    const allTiles = await getAllTiles()
    const tilesWithPriority = allTiles.map(t => ({
      ...t,
      metadata: t.metadata ?? {},
      priority: (t.metadata?.priority as 'high' | 'medium' | 'low') ?? 'medium',
    }))
    tilesWithPriority.sort((a, b) => {
      const statusOrder = { active: 0, pending: 1, answered: 2, recalculating: 3 }
      const priorityOrder = { high: 0, medium: 1, low: 2 }
      
      if (a.status !== b.status) {
        return (statusOrder[a.status as keyof typeof statusOrder] ?? 99) - 
               (statusOrder[b.status as keyof typeof statusOrder] ?? 99)
      }
      return (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99)
    })
    setTiles(tilesWithPriority)
    setIsLoading(false)
  }, [])

  useEffect(() => {
    loadTiles()
  }, [loadTiles])

  const handleAddTask = async () => {
    if (!newTaskTitle.trim()) return
    
    const now = Date.now()
    const sessionId = `session-${generateTileId()}`
    
    const session = {
      id: sessionId,
      taskId: generateTileId(),
      title: newTaskTitle,
      activeIndex: 0,
      status: 'active' as const,
      syncState: 'pending' as const,
      createdAt: now,
      updatedAt: now,
      totalSteps: 1,
      currentStep: 1,
      metadata: { source: 'inbox' },
    }
    await saveSession(session)
    
    const tile = createEmptyTile(sessionId, 0, newTaskTitle, [
      'Start working on this',
      'Break down into subtasks',
      'Research approach first',
      'Delegate / ask for help',
    ])
    
    tile.metadata = {
      ...tile.metadata,
      priority: newTaskPriority,
      project: newTaskProject,
      source: 'inbox',
    }
    
    await saveTile(tile)
    
    setNewTaskTitle('')
    setNewTaskProject('')
    setNewTaskPriority('medium')
    setShowAddModal(false)
    
    loadTiles()
  }

  const handleDeleteTask = async (tileId: string) => {
    if (!confirm('Delete this task?')) return
    await deleteTile(tileId)
    loadTiles()
  }

  const handlePriorityChange = async (tile: Tile, newPriority: 'high' | 'medium' | 'low') => {
    const updatedTile = {
      ...tile,
      metadata: { ...tile.metadata, priority: newPriority },
      updatedAt: Date.now(),
    }
    await saveTile(updatedTile)
    loadTiles()
  }

  const handleStartFocusFlow = async (tile: Tile) => {
    const updatedTile = {
      ...tile,
      status: 'active' as const,
      updatedAt: Date.now(),
    }
    await saveTile(updatedTile)
    loadTiles()
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="w-12 h-12 border-4 border-accent-primary border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <header className="flex h-16 items-center justify-between px-6 border-b border-border">
        <h1 className="text-xl font-semibold">Inbox</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary"
        >
          <PlusIcon className="w-4 h-4 mr-2" /> Add Task
        </button>
      </header>

      <main className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto space-y-3">
          <AnimatePresence>
            {tiles.length === 0 ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-12 text-text-muted"
              >
                <InboxIcon className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p className="text-lg">Inbox is empty</p>
                <p className="text-sm mt-1">Click "Add Task" to create your first task</p>
              </motion.div>
            ) : (
              tiles.map((tile, index) => (
                <motion.div
                  key={tile.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="card-hover group"
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 mt-1 flex items-center justify-center">
                      <div
                        className={`w-2 h-2 rounded-full ${PRIORITY_COLORS[tile.metadata?.priority as keyof typeof PRIORITY_COLORS] ?? PRIORITY_COLORS.medium}`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-text-primary truncate">{tile.question || String(tile.metadata?.title) || 'Untitled Task'}</h3>
                      <div className="flex items-center gap-2 mt-1 text-sm text-text-muted">
                        {tile.metadata?.project ? (
                          <span className="badge-info">{String(tile.metadata.project)}</span>
                        ) : null}
                        <span className={`badge-${(tile.metadata?.priority as string) === 'high' ? 'error' : (tile.metadata?.priority as string) === 'medium' ? 'warning' : 'success'}`}>
                          {(tile.metadata?.priority as string) ?? 'medium'}
                        </span>
                        <span className="text-text-muted capitalize">{tile.status}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleStartFocusFlow(tile)}
                        className="btn-icon btn-secondary"
                        title="Start Focus Flow"
                      >
                        <PlayIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handlePriorityChange(tile, 
                          tile.metadata?.priority === 'high' ? 'medium' : 
                          tile.metadata?.priority === 'medium' ? 'low' : 'high'
                        )}
                        className="btn-icon btn-secondary"
                        title="Cycle Priority"
                      >
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteTask(tile.id)}
                        className="btn-icon btn-secondary text-error hover:bg-red-500/10"
                        title="Delete"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </AnimatePresence>
        </div>
      </main>

      <AnimatePresence>
        {showAddModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowAddModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-md bg-card rounded-2xl border border-border p-6 space-y-4"
              onClick={e => e.stopPropagation()}
            >
              <h2 className="text-lg font-semibold">New Task</h2>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary">Task Title</label>
                <input
                  type="text"
                  value={newTaskTitle}
                  onChange={e => setNewTaskTitle(e.target.value)}
                  placeholder="What needs to be done?"
                  className="w-full px-3 py-2 rounded-xl border border-border bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                  autoFocus
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary">Project (optional)</label>
                <input
                  type="text"
                  value={newTaskProject}
                  onChange={e => setNewTaskProject(e.target.value)}
                  placeholder="Project name"
                  className="w-full px-3 py-2 rounded-xl border border-border bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-accent-primary/50"
                />
              </div>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-text-secondary">Priority</label>
                <div className="flex gap-2">
                  {(['high', 'medium', 'low'] as const).map(p => (
                    <button
                      key={p}
                      onClick={() => setNewTaskPriority(p)}
                      className={`flex-1 px-3 py-2 rounded-xl border transition-all duration-200 text-sm ${
                        newTaskPriority === p
                          ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                          : 'border-border hover:border-border/50'
                      }`}
                    >
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAddTask}
                  disabled={!newTaskTitle.trim()}
                  className="btn-primary flex-1"
                >
                  Add Task
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function PlusIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  )
}

function PlayIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
}

function ArrowUpIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
    </svg>
  )
}

function TrashIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  )
}

function InboxIcon({ className = '' }: { className?: string }) {
  return (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className={className}>
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  )
}